import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logos } from '../mock';

// Three themes. The GOLD theme is the user's preferred variant; its on-gold
// text colour has been deepened for clear readability and the background made
// vibrant/metallic.
export const THEMES = {
  indigo: {
    name: 'Indigo',
    nextLabel: 'Maroon',
    nav: '#1c1b3a',
    navText: '#f3d98b',
    navHover: '#ffe8a8',
    brand: '#e9c46a',
    onDark: '#e9c46a',
    accent: '#e0b94a',
    accentText: '#1c1b3a',
    heroAccent: '#f3d98b',
    heroAccentText: '#1c1b3a',
    body: '#0f0e22',
    bodyText: '#f4ecd8',
    cardBg: 'rgba(28, 27, 58, 0.85)',
    cardBorder: 'rgba(233, 196, 106, 0.25)',
    cardText: '#f4ecd8',
    muted: '#cdbfa1',
    overlay: 'linear-gradient(180deg, rgba(15,14,34,0.1) 0%, rgba(15,14,34,0.15) 45%, rgba(15,14,34,0.75) 100%)',
    sectionAlt: '#161533',
    logo: logos.indigo,
    logoPadding: '7%',
  },
  maroon: {
    name: 'Maroon',
    nextLabel: 'Gold',
    nav: '#2a0e12',
    navText: '#f1c878',
    navHover: '#ffe1a3',
    brand: '#e9c46a',
    onDark: '#e9c46a',
    accent: '#e0b94a',
    accentText: '#2a0e12',
    heroAccent: '#f3d98b',
    heroAccentText: '#2a0e12',
    body: '#1a070a',
    bodyText: '#f4ecd8',
    cardBg: 'rgba(42, 14, 18, 0.88)',
    cardBorder: 'rgba(233, 196, 106, 0.3)',
    cardText: '#f4ecd8',
    muted: '#d6c4a3',
    overlay: 'linear-gradient(180deg, rgba(26,7,10,0.1) 0%, rgba(26,7,10,0.18) 45%, rgba(26,7,10,0.78) 100%)',
    sectionAlt: '#23090d',
    logo: logos.maroon,
    logoPadding: '7%',
  },
  // GOLD — antique bronze-amber with aged patina sheen.
  // Richer/deeper than sun-yellow, with metallic highlights that mirror the
  // crest. Text remains deep ink for crystal-clear legibility.
  gold: {
    name: 'Gold',
    nextLabel: 'Indigo',
    // Polished antique brass strip for the nav
    nav: 'radial-gradient(ellipse at 20% 30%, rgba(255,236,180,0.55) 0%, transparent 55%), linear-gradient(135deg, #c89a40 0%, #8a5e1a 50%, #b8860b 100%)',
    navText: '#1a0508',
    navHover: '#3d0f15',
    brand: '#1a0508',
    onDark: '#e8c468',
    accent: '#1a0508',
    accentText: '#f5e4b8',
    heroAccent: '#e8c468',
    heroAccentText: '#1a0508',
    // Body: aged bronze patina with diagonal sheen + corner highlights
    body: 'radial-gradient(circle at 12% 8%, rgba(248,228,170,0.6) 0%, transparent 50%), radial-gradient(circle at 88% 92%, rgba(95,62,18,0.55) 0%, transparent 55%), linear-gradient(135deg, #b8860b 0%, #8a5e1a 50%, #ad7a18 100%)',
    bodyText: '#1a0508',
    // Cards: lighter warm patina cream so body copy reads clearly
    cardBg: 'linear-gradient(135deg, #f5e4b8 0%, #d4b673 100%)',
    cardBorder: 'rgba(26,5,8,0.32)',
    cardText: '#1a0508',
    muted: '#3a1808',
    overlay: 'linear-gradient(180deg, rgba(26,5,8,0.08) 0%, rgba(26,5,8,0.15) 45%, rgba(26,5,8,0.72) 100%)',
    // Alt section: deeper aged bronze band
    sectionAlt: 'radial-gradient(circle at 75% 25%, rgba(245,225,170,0.45) 0%, transparent 55%), linear-gradient(135deg, #8a5e1a 0%, #6e4b12 50%, #a07020 100%)',
    logo: logos.gold,
    logoPadding: '0%',
  },
};

const order = ['indigo', 'maroon', 'gold'];

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [themeKey, setThemeKey] = useState(() => {
    try {
      const saved = localStorage.getItem('svf-theme');
      if (saved && THEMES[saved]) return saved;
    } catch (e) { /* ignore */ }
    return 'indigo';
  });

  const theme = THEMES[themeKey];

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([k, v]) => {
      if (typeof v === 'string') root.style.setProperty(`--svf-${k}`, v);
    });
    document.body.style.background = theme.body;
    document.body.style.color = theme.bodyText;
    try { localStorage.setItem('svf-theme', themeKey); } catch (e) { /* ignore */ }
  }, [theme, themeKey]);

  const cycleTheme = useCallback(() => {
    setThemeKey((curr) => {
      const idx = order.indexOf(curr);
      return order[(idx + 1) % order.length];
    });
  }, []);

  const value = useMemo(() => ({ theme, themeKey, cycleTheme, setThemeKey }), [theme, themeKey, cycleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
